import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import DocumentModal from '../components/DocumentModal';
import { supabaseStorageService } from '../services/supabaseStorage';

const { FiPlus, FiFileText, FiFolder, FiLock, FiDownload, FiTrash2, FiEdit, FiSearch } = FiIcons;

export default function Documents() {
  const { state, dispatch } = useFamilyContext();
  const { documents } = state;
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [storageInfo, setStorageInfo] = useState(null);

  const categories = ['All', 'Versicherungen', 'Finanzen', 'Medizinisch', 'Haus', 'Fahrzeug', 'Allgemein'];

  const filteredDocs = (documents || []).filter(doc => {
    const matchesCategory = filterCategory === 'All' || doc.category === filterCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          doc.notes?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Storage-Info laden
  const loadStorageInfo = async () => {
    try {
      const info = await supabaseStorageService.getBucketInfo();
      setStorageInfo(info);
    } catch (error) {
      console.error('Storage info error:', error);
    }
  };

  useEffect(() => {
    loadStorageInfo();
  }, []);

  const handleSave = (docData) => {
    if (editingDoc) {
      dispatch({ type: 'UPDATE_DOCUMENT', payload: { ...editingDoc, ...docData } });
      setEditingDoc(null);
    } else {
      dispatch({ type: 'ADD_DOCUMENT', payload: docData });
    }
  };

  const handleDelete = async (doc) => {
    if (confirm('Dokument und Datei entfernen?')) {
      try {
        // Datei aus Storage löschen, falls vorhanden
        if (doc.filePath) {
          await supabaseStorageService.deleteFile(doc.filePath);
        }
        
        // Dokument aus DB löschen
        dispatch({ type: 'DELETE_DOCUMENT', payload: doc.id });
        
        // Storage-Info aktualisieren
        loadStorageInfo();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Löschen fehlgeschlagen: ' + error.message);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dokumente</h1>
          <p className="text-gray-600 mt-2">Sichere Ablage für wichtige Unterlagen</p>
        </div>
        <button 
          onClick={() => { setEditingDoc(null); setShowModal(true); }} 
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mt-4 sm:mt-0"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Neues Dokument
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                filterCategory === cat 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="Suchen..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDocs.map((doc, index) => (
          <motion.div
            key={doc.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex space-x-1">
                <button 
                  onClick={() => { setEditingDoc(doc); setShowModal(true); }} 
                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                >
                  <SafeIcon icon={FiEdit} className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(doc)} 
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>

            <h3 className="font-bold text-gray-900 mb-1 truncate" title={doc.title}>{doc.title}</h3>
            
            <div className="flex items-center text-xs text-gray-500 mb-4 space-x-3">
              <span className="flex items-center">
                <SafeIcon icon={FiFolder} className="w-3 h-3 mr-1" />
                {doc.category}
              </span>
              {doc.isProtected && (
                <span className="flex items-center text-orange-600">
                  <SafeIcon icon={FiLock} className="w-3 h-3 mr-1" />
                  Geschützt
                </span>
              )}
            </div>

            {doc.notes && (
              <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
                {doc.notes}
              </p>
            )}

            <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
               <span className="text-xs text-gray-400">
                 {doc.fileUrl ? 'Datei vorhanden' : 'Nur Eintrag'}
               </span>
               {doc.fileUrl && (
                 <a 
                   href={doc.fileUrl} 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                 >
                   <SafeIcon icon={FiDownload} className="w-4 h-4 mr-1" />
                   Öffnen
                 </a>
               )}
            </div>
          </motion.div>
        ))}

        {filteredDocs.length === 0 && (
          <div className="col-span-full py-12 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <SafeIcon icon={FiFolder} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Keine Dokumente gefunden.</p>
          </div>
        )}
      </div>

      {showModal && (
        <DocumentModal 
          doc={editingDoc} 
          onClose={() => setShowModal(false)} 
          onSave={handleSave} 
        />
      )}
    </div>
  );
}